import styled from '@emotion/styled';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect } from 'react';

import { formatMoney, presentMoneyRange } from './functions/sharedFunctions';

const P = styled.p`
  color: #000000;
  font-size: 14px;
  margin: 8px 0 0px;
  text-transform: uppercase;
`;

const Header = styled.div`
  margin: 0 16px 0 16px;
`;

const Info = styled.span`
  font-size: 12px;

  &:last-child {
    margin-bottom: 10px;
  }

  &.margin-b-16 {
    margin-bottom: 16px;
  }

  &.margin-b-8 {
    margin-bottom: 8px;
  }

  &.margin-b-5 {
    margin-bottom: 5px;
  }

  &.margin-b-0 {
    margin-bottom: 0px;
  }

  &.font-8 {
    font-size: 8px;
  }
`;

const QuoteEstimated = styled.p`
  align-items: end;
  color: #000000;
  display: flex;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  margin: 6px 0px;
  text-align: right;
`;

const Contingencies = styled.p`
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  color: #000000;
  margin: 6px 0px;
`;

const Container = styled.div`
  border-top: 2px solid black;
  display: flex;
  flex-direction: column;
  h2 {
    margin: 16px 0 0 0;
  }

  .padding-tp-6 {
    padding-top: 6px;
  }
`;

const View = styled.div`
  width: 612px;
  height: 900px;
  margin: auto;
  border: 1px solid #ffffff;
  .pink {
    color: #d40f7d;
  }
`;

const ViewWrapper = styled.div`
  margin: 32px 32px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 2px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  h4 {
    margin: auto !important;
  }
  &.gap-0 {
    gap: 0px;
  }
`;
const Section = styled.div`
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(3, 1fr);
  justify-content: space-between;
  padding: 16px;
`;

const CentralizedSection = styled.div`
  margin: 0 16px 0 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const SubTitle = styled.strong`
  color: #000000;
  font-size: 12px;
  margin-bottom: 5px;
`;

const Title = styled.p`
  font-weight: 700;
  margin-bottom: 0;

  &.pink {
    color: #d40f7d;
  }

  &.margin-top-8 {
    margin-top: 8px;
  }

  &.margin-top-16 {
    margin-top: 16px;
  }

  &.margin-top-24 {
    margin-top: 24px;
  }
`;

const TitleWithContingencies = styled.p`
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  margin: 6px 0px;
`;

const ContingenciesWrapper = styled.div`
  border-top: 0.5px solid #6d6d6d;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ContingenciesContent = styled.p`
  color: #000000;
  font-size: 12px;
  margin: 6px 16px;
`;

const Address = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 2px;
`;

const generatePDF = (id, title) => {
  const input = document.getElementById(id);
  const canvas = document.createElement('canvas');
  canvas.width = 1228;
  canvas.height = 2000;
  const options = {
    canvas,
    scale: 2,
    width: 1224,
    height: 1800,
    windowWidth: 1224,
    windowHeight: 1800,
  };

  html2canvas(input, options).then(canvas => {
    const imgData = canvas.toDataURL('image/png');

    // generate pdf and take its measures
    const pdf = new jsPDF({
      orientation: 'portrait',
      format: [1224, 1800],
      unit: 'px',
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    // input data on pdf save
    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pageHeight);
    pdf.save(`${title}.pdf`);
  });
};

export const QuotingWidgetPdf = ({
  totalEstimationWithoutContingencies,
  totalEstimationWithContingencies,
  quoteEstimation,
  contactInfo,
  setDownloadPdf,
  roofDetails,
  contractorName,
  ...props
}) => {
  useEffect(() => {
    generatePDF('printPDF', 'pdf_download');
    setDownloadPdf(false);
  });

  const additionalStructures = roofDetails.find(details => details.inputField === 'additional-structures-with-size')
    ?.value;

  return (
    <View id="printPDF">
      <ViewWrapper>
        <Container>
          <Header>
            <Title className="margin-top-16" font="body">
              Roofing Quote Estimation
            </Title>
            <P>{contractorName}</P>
            <ContactInfo>
              <Address>
                <Info className="margin-b-0">
                  {props.contractorPhone} • {props.contractorEmail}
                </Info>
              </Address>
            </ContactInfo>
          </Header>
        </Container>

        <Container>
          <Section>
            <Wrapper className="gap-0">
              <SubTitle>Location:</SubTitle>
              <Info
                dangerouslySetInnerHTML={{
                  __html: contactInfo?.find(data => data.inputField === 'address').value,
                }}
              />
            </Wrapper>

            <Wrapper className="gap-0">
              <SubTitle>Structures:</SubTitle>
              <Info>Main House: </Info>
              <Info className="margin-b-5">
                {roofDetails.find(details => details.inputField === 'area').value} sq.ft
              </Info>
              {Object.keys(additionalStructures).map(structure => (
                <>
                  <Info>{additionalStructures[structure].label}:</Info>
                  <Info className="margin-b-5">{additionalStructures[structure].value} sq.ft</Info>
                </>
              ))}
            </Wrapper>

            <Wrapper className="gap-0">
              <SubTitle>Shingle Selection:</SubTitle>
              <Info>Type:</Info>
              <Info className="margin-b-5">Owens Corning {props.shingleInfo.shingleLine}</Info>
              <Info>Color:</Info>
              <Info className="margin-b-5">{props.shingleInfo.shingleColor}</Info>
            </Wrapper>
          </Section>
        </Container>

        <Container>
          <CentralizedSection>
            <Wrapper className="gap-0">
              <Title className="pink margin-top-8" font="body">
                Quote Estimation
              </Title>
              <Info className="margin-b-8 font-8">Contingencies not included </Info>
            </Wrapper>

            <Wrapper>
              <Title className="pink">
                {presentMoneyRange(totalEstimationWithoutContingencies[0], totalEstimationWithoutContingencies[1])}
              </Title>
            </Wrapper>
          </CentralizedSection>
        </Container>

        <Container>
          <CentralizedSection>
            <Wrapper>
              <Contingencies>Cost Breakdown</Contingencies>
            </Wrapper>
          </CentralizedSection>
        </Container>

        <ContingenciesWrapper>
          <ContingenciesContent>Main House </ContingenciesContent>
          <ContingenciesContent>
            {presentMoneyRange(quoteEstimation.minEstimate, quoteEstimation.maxEstimate)}
          </ContingenciesContent>
        </ContingenciesWrapper>
        {Object.keys(additionalStructures).map(structure => (
          <ContingenciesWrapper>
            <ContingenciesContent> {additionalStructures[structure].label}</ContingenciesContent>
            <ContingenciesContent>
              {presentMoneyRange(
                quoteEstimation.additionalStructures[structure].minEstimate,
                quoteEstimation.additionalStructures[structure].maxEstimate
              )}
            </ContingenciesContent>
          </ContingenciesWrapper>
        ))}

        <Container>
          <span style={{ margin: '17px' }} />
        </Container>

        <Container>
          <CentralizedSection>
            <Wrapper>
              <TitleWithContingencies className="pink">Quote Estimation with all contingencies</TitleWithContingencies>
            </Wrapper>

            <Wrapper>
              <QuoteEstimated className="pink">
                {presentMoneyRange(totalEstimationWithContingencies()[0], totalEstimationWithContingencies()[1])}
              </QuoteEstimated>
            </Wrapper>
          </CentralizedSection>
        </Container>

        {quoteEstimation?.contingencies && (
          <Container>
            <CentralizedSection>
              <Wrapper>
                <Contingencies>Contingencies</Contingencies>
              </Wrapper>

              <Wrapper>
                <Contingencies>Possible Additional Costs</Contingencies>
              </Wrapper>
            </CentralizedSection>
          </Container>
        )}

        {quoteEstimation?.contingencies &&
          quoteEstimation.contingencies.map(contingency => (
            <ContingenciesWrapper>
              <ContingenciesContent> {contingency.name}</ContingenciesContent>
              <ContingenciesContent>{formatMoney(contingency.cost)}</ContingenciesContent>
            </ContingenciesWrapper>
          ))}

        <Container className="padding-tp-6">
          <Title className="margin-top-24" font="body">
            Contact {contractorName} Today!
          </Title>
          <ContactInfo>
            <Address>
              <Info>
                {props.contractorPhone} • {props.contractorEmail}
              </Info>
            </Address>
          </ContactInfo>
        </Container>
      </ViewWrapper>
    </View>
  );
};
